<script setup lang="ts">
interface Props {
  media: Media
  autoplay?: boolean
  uniform?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  autoplay: true,
  uniform: false,
})

const isVideo = computed(() => isVideoMedia(props.media))

// An image is its own preview and a PDF has one rasterised for it; every other
// document has none, and falls through to the sheet.
const previewImage = computed(() => mediaPreviewImage(props.media))

const aspectRatio = computed(() => {
  if (props.uniform) { return undefined }
  if (!previewImage.value) { return `${mediaAspectRatio(props.media)}` }
  if (!previewImage.value.width || !previewImage.value.height) { return undefined }

  switch (getOrientation(previewImage.value.width, previewImage.value.height)) {
    case 'landscape':
      return '16 / 11'
    case 'portrait':
      return '3 / 4'
    case 'square':
      return '1 / 1'
    default:
      return undefined
  }
})
</script>

<template>
  <video
    v-if="isVideo"
    class="w-full h-full object-cover pointer-events-none"
    :src="media.url ?? undefined"
    :poster="media.thumbnailURL ?? undefined"
    :style="{ aspectRatio }"
    :autoplay="autoplay"
    muted
    :loop="autoplay"
    playsinline
    preload="metadata"
  />
  <core-image
    v-else-if="previewImage"
    :image="previewImage"
    :apply-aspect-ratio-style="!uniform"
    class="w-full h-full object-contain"
  />
  <core-media-preview-file
    v-else
    :media="media"
    compact
    :style="{ aspectRatio }"
  />
</template>
